import type {
  SummarizeLength as ContractSummarizeLength,
  TranslateTarget as ContractTranslateTarget,
} from '@iq/contracts/v1/chat';
import {
  BookOpen,
  CloudSun,
  FileText,
  Globe,
  HelpCircle,
  Languages,
  type LucideIcon,
} from 'lucide-react';

/**
 * Single source of truth for every slash-command entry in the composer
 * palette and the `/help` modal. Both the palette UI and the
 * `DirectiveChip` formatter consume this map so the displayed name and
 * description stay in lock-step across the app.
 *
 * The `id` is the directive id — the same string the backend sees in
 * `metadata.custom.directive`.
 *
 * Tool commands (`kind: 'tool'`) use auto-chip insertion to bias the LLM.
 * Action commands (`kind: 'action'`) take a custom flow via the
 * action-param popover (`summarize` + `translate`) or the help modal
 * (`help`).
 */

export type CommandKind = 'tool' | 'action';

export type CommandId =
  | 'get_weather'
  | 'web_search'
  | 'search_knowledge_base'
  | 'summarize'
  | 'translate'
  | 'help';

export interface CommandMeta {
  id: CommandId;
  kind: CommandKind;
  icon: LucideIcon;
  labelKey: string;
  descriptionKey: string;
}

export const COMMAND_META: ReadonlyArray<CommandMeta> = [
  {
    id: 'get_weather',
    kind: 'tool',
    icon: CloudSun,
    labelKey: 'chat.commands.get_weather.label',
    descriptionKey: 'chat.commands.get_weather.description',
  },
  {
    id: 'web_search',
    kind: 'tool',
    icon: Globe,
    labelKey: 'chat.commands.web_search.label',
    descriptionKey: 'chat.commands.web_search.description',
  },
  {
    id: 'search_knowledge_base',
    kind: 'tool',
    icon: BookOpen,
    labelKey: 'chat.commands.search_knowledge_base.label',
    descriptionKey: 'chat.commands.search_knowledge_base.description',
  },
  {
    id: 'summarize',
    kind: 'action',
    icon: FileText,
    labelKey: 'chat.commands.summarize.label',
    descriptionKey: 'chat.commands.summarize.description',
  },
  {
    id: 'translate',
    kind: 'action',
    icon: Languages,
    labelKey: 'chat.commands.translate.label',
    descriptionKey: 'chat.commands.translate.description',
  },
  {
    id: 'help',
    kind: 'action',
    icon: HelpCircle,
    labelKey: 'chat.commands.help.label',
    descriptionKey: 'chat.commands.help.description',
  },
];

export function commandById(id: string): CommandMeta | undefined {
  return COMMAND_META.find((m) => m.id === id);
}

export function isToolCommand(id: string): boolean {
  return commandById(id)?.kind === 'tool';
}

/**
 * Tools accessible via the composer's `@` mention palette. Tools live
 * behind `@`, action commands behind `/`.
 */
export function listMentionTools(): ReadonlyArray<CommandMeta> {
  return COMMAND_META.filter((m) => m.kind === 'tool');
}

/**
 * Action commands accessible via the composer's `/` slash palette.
 */
export function listSlashActions(): ReadonlyArray<CommandMeta> {
  return COMMAND_META.filter((m) => m.kind === 'action');
}

/**
 * Sub-option choices for the `/summarize` action-param popover. The `id`
 * mirrors the contract enum `SummarizeLength` from `@iq/contracts/v1/chat`.
 */
export const SUMMARIZE_LENGTHS: ReadonlyArray<{
  id: ContractSummarizeLength;
  labelKey: string;
  hintKey: string;
}> = [
  {
    id: 'short',
    labelKey: 'chat.commands.summarize.length.short.label',
    hintKey: 'chat.commands.summarize.length.short.hint',
  },
  {
    id: 'medium',
    labelKey: 'chat.commands.summarize.length.medium.label',
    hintKey: 'chat.commands.summarize.length.medium.hint',
  },
  {
    id: 'long',
    labelKey: 'chat.commands.summarize.length.long.label',
    hintKey: 'chat.commands.summarize.length.long.hint',
  },
];

/**
 * Sub-option choices for the `/translate` action-param popover. The `id`
 * mirrors the contract enum `TranslateTarget` from `@iq/contracts/v1/chat`.
 */
export const TRANSLATE_TARGETS: ReadonlyArray<{
  id: ContractTranslateTarget;
  labelKey: string;
}> = [
  { id: 'en', labelKey: 'chat.commands.translate.target.en' },
  { id: 'de', labelKey: 'chat.commands.translate.target.de' },
  { id: 'fr', labelKey: 'chat.commands.translate.target.fr' },
  { id: 'es', labelKey: 'chat.commands.translate.target.es' },
];
